declare global {
  interface Navigator {
    msSaveBlob: (blobOrBase64: Blob | string, filename: string) => void
  }
}

import * as $ from 'jquery';
import { Serializer } from 'common/serializer';
import { IDisposable, disposable, serialDisposable, compositeDisposable, disposableSubscription } from 'common/disposable';
import { Observable } from 'common/barrels/rx';

import { AuthenticationManager } from 'common/authenticationmanager';
import { Unauthorized } from 'common/messages/unauthorized';
import { AccessDenied } from './exceptions/accessdenied';

const authenticationManager = new AuthenticationManager();

export class Request {
    private url: string;

    private beforeLambda: () => void = () => { };
    private handlers: Handler<any>[];
    private finalLambda: () => void = () => { };
    private argsLambda: () => any = null;
    private errorLambda: (jqXhr: JQueryXHR, textStatus: string, errorThrown: string) => void;
    private unknownLambda: (x: any) => void = null;

    constructor(public serializer: Serializer, url: string) {
        this.url = url;
        this.handlers = [];
    }

    static create(serializer: Serializer, url: string): Request {
        return new Request(serializer, url);
    }

    args(argsLambda: () => any): Request {
        this.argsLambda = argsLambda;
        return this;
    }

    before(beforeLambda: () => void): Request {
        this.beforeLambda = beforeLambda;
        return this;
    }

    handle<T>(type: { new (...args: any[]): T }, lambda: (t: T, a: any) => void): Request {
        this.handlers.push(new Handler(type, lambda));
        return this;
    }

    handleVoid(lambda: (x: void) => void): Request {
        this.handlers.push(new Handler(null, lambda));
        return this;
    }

    handleArray<T>(type: { new (...args: any[]): T }, lambda: (t: T[], a: any) => void): Request {
        this.handlers.push(new ArrayHandler(type, lambda));
        return this;
    }

    retry<T>(type: { new (...args: any[]): T }, lambda: (t: T) => void, maxRetries: number = 1): Request {
        this.handlers.push(new RetryHandler(type, lambda, maxRetries));
        return this;
    }

    final(finalLambda: () => void): Request {
        this.finalLambda = finalLambda;
        return this;
    }

    unknown(unknownLambda: (x: any) => void): Request {
        this.unknownLambda = unknownLambda;
        return this;
    }

    error(errorLambda: (jqXhr: JQueryXHR, textStatus: string, errorThrown: string) => void): Request {
        this.errorLambda = errorLambda;
        return this;
    }

    post(csrfToken: string, cancel?: Observable<any>, ...files: FileParameter[]): IDisposable {
        const cancelDisposable = this.makeRequest('POST', false, csrfToken, files);

        if (cancel)
            cancel.first().subscribe(() => cancelDisposable.dispose());

        return cancelDisposable;
    }

    postDownload(csrfToken: string, cancel?: Observable<any>, ...files: FileParameter[]) {
        const cancelDisposable = this.makeRequest('POST', true, csrfToken, files);

        if (cancel)
            cancel.first().subscribe(() => cancelDisposable.dispose());

        return cancelDisposable;
    }

    'get'(cancel?: Observable<any>): IDisposable {
        const cancelDisposable = this.makeRequest('GET', false);

        if (cancel)
            cancel.first().subscribe(() => cancelDisposable.dispose());

        return cancelDisposable;
    }

    private makeRequest(verb: string, download: boolean, csrfToken: string = null, files?: FileParameter[]): IDisposable {
        let act: any = null;
        let data: any = null;

        let authenticating = false;
        const authDisposable = serialDisposable(null);

        const success = (rawResult: any) => {
            const voidHandler = this.handlers.find(x => x.type === null);

            if ((rawResult === undefined || rawResult === null || isEmptyArray(rawResult.$values)) && voidHandler !== null && voidHandler !== undefined) {
                voidHandler.lambda(null, data);
            } else {
                handleResult(rawResult);
            }

            this.finalLambda();
        };

        const error = (jqXhr: JQueryXHR, textStatus: string, errorThrown: string): void => {
            handleResult(jqXhr.responseJSON);
            this.finalLambda();

            if (this.errorLambda !== null && this.errorLambda !== undefined) {
                this.errorLambda(jqXhr, textStatus, errorThrown);
            } else {
                if (jqXhr.status !== 0)
                    throw new Error('Unknown error!');
            }
        };

        const handleResult = (rawResult:any) => {
            const result = this.serializer.read(typeof rawResult === 'string' ? JSON.parse(rawResult) : rawResult);

            if (result instanceof Unauthorized && !authenticating) {
                authenticating = true;
                authDisposable.set(disposableSubscription(authenticationManager.authenticate(result.authUri).subscribe(() => act())));
                return;
            }

            const handler = this.handlers.filter(x => x.type !== null).find(x => result instanceof x.type || isArrayOf(x.type, result) || (isEmptyArray(result) && x instanceof ArrayHandler));
            if (handler !== null && handler !== undefined) {
                handler.lambda(result, data);

                if (handler instanceof RetryHandler) {
                    if (handler.shouldRetry()) {
                        handler.incrementRetries();
                        handler.lambda(null);
                        act();
                        return;
                    } else {
                        throw new Error('Max retries exceeded!');
                    }
                }
            } else {
                if (this.unknownLambda !== null) {
                    this.unknownLambda(result);
                }
                else {
                    this.finalLambda();
                    throw new Error('Unknown response!' + (result && result.constructor && result.constructor.name ? ' (type: ' + result.constructor.name + ')' : ''));
                }
            }
        }

        this.beforeLambda();

        const dataLambda = () => this.argsLambda === null ? null : this.argsLambda();

        const noFiles = (files === undefined || files === null || files.length === 0);

        let pendingRequest: IDisposable = disposable(() => { });

        act = download ? () => pendingRequest = this.downloadRequest(data = dataLambda(), success, error, csrfToken)
            : noFiles ? () => pendingRequest = this.nonFileRequest(verb, data = dataLambda(), success, error, () => this.finalLambda(), csrfToken)
            : () => pendingRequest = this.fileRequest(JSON.stringify(data = dataLambda()), files, success, error, () => this.finalLambda(), csrfToken);

        act();

        // the reason this doesn't just return pendingRequest is that retries may actually cause it to be reassigned
        // return disposable(() => pendingRequest.dispose());
        return compositeDisposable([
            disposable(() => pendingRequest.dispose()),
            authDisposable
        ]);
    }

    private nonFileRequest(
        verb: string,
        data: any,
        successHandler: (data: any, textStatus: string, jqXhr: JQueryXHR) => void,
        errorHandler: (jqXhr: JQueryXHR, textStatus: string, errorThrown: string) => void,
        finalizer: () => void,
        csrfToken: string
    ): IDisposable {
        let aborted = false;
        const fragment = window.location.hash ? window.location.hash.substring(1) : '';
        const inIframe = window !== window.parent;
        const headers = verb === 'POST' && csrfToken !== null ? { 'workflow-csrf-token': csrfToken,  'fragment': fragment, 'inIframe': inIframe } : { 'fragment': fragment, 'inIframe': inIframe };

        const xhr = $.ajax({
            accepts: 'application/json',
            type: verb,
            url: this.url,
            data: !data ? null : (verb === 'GET') ? data : JSON.stringify(data),
            success: successHandler,
            contentType: 'application/json',
            dataType: 'json',
            error: (x, t, e) => {

                //special handling for 403s, these are usually status codes only and not have responseJSON populated
                if (x.status === 403) {
                    successHandler(x.responseJSON ?? new AccessDenied(), 'Access Denied', x);
                    return;
                }

                // 400 is no longer considered an error - all "you messed up, here is your message" results are now 400s, not 200s. The client SHOULD be handling such things.
                // 401 has special behaviour, handled elsewhere in this file
                if (x.status === 400 || x.status === 401) {
                    successHandler(x.responseJSON, '???', x);
                    return;
                }

                if (!aborted)
                    errorHandler(x, t, e);
                else
                    finalizer();
            },
            global: false,
            headers: headers
        });

        return disposable(() => {
            aborted = true;
            xhr.abort();
        });
    }

    private fileRequest(
        uploadArgs: string,
        files: FileParameter[],
        successHandler: (data: any, textStatus: string, jqXhr: JQueryXHR) => void,
        errorHandler: (jqXhr: JQueryXHR, textStatus: string, errorThrown: string) => void,
        finalizer: () => void,
        csrfToken: string
    ): IDisposable {
        const formData = new FormData();

        files.forEach(x => formData.append(x.name, x.data, x.fileName));

        formData.append('args', uploadArgs);

        let aborted = false;
        const xhr = $.ajax({
            type: 'POST',
            url: this.url,
            data: formData,
            success: successHandler,
            processData: false,
            contentType: false,
            error: (x, t, e) => {
                if (!aborted)
                    errorHandler(x, t, e);
                else
                    finalizer();
            },
            headers: { 'workflow-csrf-token': csrfToken }
        });

        return disposable(() => {
            aborted = true;
            xhr.abort();
        });
    }

    private downloadRequest(
        args: any,
        successHandler: (data: any, textStatus: string, jqXhr: JQueryXHR) => void,
        errorHandler: (jqXhr: JQueryXHR, textStatus: string, errorThrown: string) => void,
        csrfToken: string
    ): IDisposable {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', this.url, true);
        xhr.responseType = 'arraybuffer';
        xhr.onload = () => {
            if (xhr.status === 200) {
                var filename = '';
                var disposition = xhr.getResponseHeader('Content-Disposition');
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    var matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
                }
                var type = xhr.getResponseHeader('Content-Type');

                var blob = new Blob([xhr.response], { type: type });

                if (blob.type === 'text/plain') {
                    // we failed; interpret response as json and dispatch as appropriate
                    var reader = new FileReader();
                    reader.addEventListener('loadend', () => successHandler(String.fromCharCode.apply(null, new Uint8Array(<ArrayBuffer>reader.result)), undefined, undefined));
                    reader.readAsArrayBuffer(blob);
                    return;
                }

                if (typeof window.navigator.msSaveBlob !== 'undefined') {
                    // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed."
                    window.navigator.msSaveBlob(blob, filename);
                    successHandler(null, undefined, undefined);
                } else {
                    var URL = window.URL || (<any>window).webkitURL;
                    var downloadUrl = URL.createObjectURL(blob);

                    if (filename) {
                        // use HTML5 a[download] attribute to specify filename
                        var a = document.createElement('a');

                        if (typeof a.download === 'undefined') {
                            (<any>window).location = downloadUrl;
                        } else {
                            a.href = downloadUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                        }
                        successHandler(null, undefined, undefined);
                    } else {
                        (<any>window).location = downloadUrl;
                    }

                    // this looks unacceptable, but I don't yet fully understand exactly how all this pieces together, and how much of it I can cull
                    // I expect I'll need to remove this when I have time to completely review this code
                    setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 100);
                }
            } else if(xhr.status === 400) { 
                // error was thrown
                // convert response in json and let successHandler handle it
                var type = xhr.getResponseHeader('Content-Type');
                var blob = new Blob([xhr.response], { type: type });
                var reader = new FileReader();
                reader.addEventListener('loadend', () => successHandler(String.fromCharCode.apply(null, new Uint8Array(<ArrayBuffer>reader.result)), undefined, undefined));
                reader.readAsArrayBuffer(blob);
                return;
            }
        };
        xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        xhr.setRequestHeader('workflow-csrf-token', csrfToken)
        xhr.send($.param(args));

        return disposable(() => xhr.abort());
    }
}

class Handler<T> {
    constructor(public type: { new (...args: any[]): T }, public lambda: (t: T, a: any) => void) { }
}

class ArrayHandler<T> {
    constructor(public type: { new (...args: any[]): T }, public lambda: (t: T[], a: any) => void) { }
}

class RetryHandler<T> extends Handler<T> {
    private retries = 0;

    constructor(public type: { new (...args: any[]): T }, public lambda: (t: T) => void, public maxRetries: number) {
        super(type, lambda);
    }

    incrementRetries() {
        this.retries++;
    }

    shouldRetry() {
        return this.retries < this.maxRetries;
    }
}

function isArrayOf(t: any, o: any): boolean {
    return Array.isArray(o) && o.length > 0 && o[0] instanceof t;
}

function isEmptyArray(o: any): boolean {
    return o && o.constructor === Array && o.length === 0;
}

export class FileParameter {
    constructor(public name: string, public data: File | Blob, public fileName?: string) { }
}